// src/pages/EventUploader.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    onSnapshot,
    doc,
} from 'firebase/firestore';
import { storage, db, app, auth } from '../firebaseConfig';           // note storage here
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';  // and these
import '../styles/SuitcaseTheatreArcadeStyle.css';
import { useAuth } from '../AuthProvider';


const EventUploader = () => {
    // Form fields
    const [eventName, setEventName] = useState('');
    const [eventDescription, setEventDescription] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventImageUrl, setEventImageUrl] = useState('');
    const [eventArtists, setEventArtists] = useState([{role: '', artist_name: ''}]);

    // State
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [editingEventId, setEditingEventId] = useState(null);
    const [events, setEvents] = useState([]);

    // File input ref for reset
    const fileInputRef = useRef(null);
    // grab the user from your AuthProvider
    const {user} = useAuth();

    // Real-time Firestore listener
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, 'events'),
            snapshot => setEvents(snapshot.docs.map(d => ({id: d.id, ...d.data()}))),
            err => console.error(err)
        );
        return () => unsubscribe();
    }, []);


    // if you render the uploader before login, bail out
    if (!user) {
        return (
            <div className="event-uploader-container">
                <p>Please <Link to="/login">log in</Link> to upload events.</p>
            </div>
        );
    }

    // Upload file to DO Space
    const handleFileChange = e => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Make sure Firebase sees you as logged in
        console.log("Attempting upload as:", auth.currentUser);
        if (!auth.currentUser) {
            setMessage("You must be signed in to upload images.");
            return;
        }

        setLoading(true);
        setMessage("");

        const path = `uploads/events/${Date.now()}_${file.name}`;
        const fileRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(fileRef, file, {
            contentType: file.type
        });

        uploadTask.on(
            "state_changed",
            snapshot => {
                // optional: track progress
            },
            err => {
                console.error("Upload failed:", err);
                setMessage("Image upload failed");
                setLoading(false);
            },
            async () => {
                const url = await getDownloadURL(uploadTask.snapshot.ref);
                setEventImageUrl(url);
                setLoading(false);
            }
        );
    };


    // Artist handlers
    const handleArtistChange = (idx, field, value) => {
        const copy = [...eventArtists];
        copy[idx][field] = value;
        setEventArtists(copy);
    };
    const addArtist = () => setEventArtists([...eventArtists, {role: '', artist_name: ''}]);
    const removeArtist = idx => setEventArtists(eventArtists.filter((_, i) => i !== idx));

    // Reset form
    const resetForm = () => {
        setEventName('');
        setEventDescription('');
        setEventDate('');
        setEventImageUrl('');
        setEventArtists([{role: '', artist_name: ''}]);
        setEditingEventId(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Submit event
    const handleSubmit = async e => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        const cleanedArtists = eventArtists.filter(a => a.role.trim() || a.artist_name.trim());
        const eventData = {
            event_name: eventName,
            event_description: eventDescription,
            event_date: new Date(eventDate).toISOString(),
            event_image_url: eventImageUrl,
            event_artists: cleanedArtists,
        };
        try {
            if (editingEventId) {
                await updateDoc(doc(db, 'events', editingEventId), eventData);
                setMessage('Event updated successfully!');
            } else {
                await addDoc(collection(db, 'events'), eventData);
                setMessage('Event uploaded successfully!');
            }
            resetForm();
        } catch (err) {
            console.error(err);
            setMessage('Error saving event.');
        } finally {
            setLoading(false);
        }
    };

    // Edit existing
    const handleEdit = ev => {
        setEditingEventId(ev.id);
        setEventName(ev.event_name);
        setEventDescription(ev.event_description);
        setEventDate(new Date(ev.event_date).toISOString().slice(0, 16));
        setEventImageUrl(ev.event_image_url);
        setEventArtists(ev.event_artists.length ? ev.event_artists : [{role: '', artist_name: ''}]);
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    // Delete event
    const handleDelete = async id => {
        if (!window.confirm('Delete this event?')) return;
        try {
            await deleteDoc(doc(db, 'events', id));
            setMessage('Event deleted');
        } catch (err) {
            console.error(err);
            setMessage('Error deleting event.');
        }
    };

    return (
        <div className="event-uploader-container">
            <h2>{editingEventId ? 'Edit Event' : 'Upload New Event'}</h2>

            <form onSubmit={handleSubmit} className="event-uploader-form">
                {/* Name */}
                <label className="event-uploader-label">
                    Event Name:
                    <input
                        type="text"
                        value={eventName}
                        onChange={e => setEventName(e.target.value)}
                        required
                        className="event-uploader-input"
                    />
                </label>

                {/* Description */}
                <label className="event-uploader-label">
                    Description:
                    <textarea
                        value={eventDescription}
                        onChange={e => setEventDescription(e.target.value)}
                        required
                        className="event-uploader-input event-uploader-textarea"
                    />
                </label>

                {/* Date */}
                <label className="event-uploader-label">
                    Date:
                    <input
                        type="datetime-local"
                        value={eventDate}
                        onChange={e => setEventDate(e.target.value)}
                        required
                        className="event-uploader-input"
                    />
                </label>

                {/* Image Upload */}
                <label className="event-uploader-label">
                    Event Image (optional):
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={loading}
                        ref={fileInputRef}
                        className="event-uploader-file-input"
                    />
                </label>
                {eventImageUrl && (
                    <div className="event-form-container">
                        <img
                            src={eventImageUrl}
                            alt="Preview"
                            className="event-uploader-preview"
                        />
                    </div>
                )}

                {/* Artists */}
                <fieldset className="event-uploader-fieldset">
                    <legend>Artists:</legend>
                    {eventArtists.map((artist, idx) => (
                        <div key={idx} className="event-uploader-artist-row">
                            <input
                                type="text"
                                placeholder="Role"
                                value={artist.role}
                                onChange={e => handleArtistChange(idx, 'role', e.target.value)}
                                required
                                className="event-uploader-input event-uploader-artist-input"
                            />
                            <input
                                type="text"
                                placeholder="Artist Name"
                                value={artist.artist_name}
                                onChange={e => handleArtistChange(idx, 'artist_name', e.target.value)}
                                required
                                className="event-uploader-input event-uploader-artist-input"
                            />
                            {eventArtists.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeArtist(idx)}
                                    className="event-uploader-remove-button"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addArtist}
                        className="event-uploader-add-button"
                    >
                        Add Artist
                    </button>
                </fieldset>

                {/* Submit & Cancel */}
                <button
                    type="submit"
                    disabled={loading}
                    className="event-uploader-submit-button"
                >
                    {loading
                        ? (editingEventId ? 'Updating...' : 'Uploading...')
                        : (editingEventId ? 'Update Event' : 'Upload Event')}
                </button>
                {editingEventId && (
                    <button
                        type="button"
                        onClick={resetForm}
                        className="event-uploader-reset-button"
                    >
                        Cancel
                    </button>
                )}
            </form>

            {message && <p className="event-uploader-message">{message}</p>}

            <h2 className="event-uploader-current-title">Current Events</h2>

            {events && events.length > 0 ? (
                <div className="event-uploader-preview-container">
                    {events.map(ev => (
                        <div key={ev.id} className="event-uploader-event-card">
                            {ev.event_image_url && (
                                <div className="event-uploader-event-image-container">
                                    <img
                                        src={ev.event_image_url}
                                        alt={ev.event_name}
                                        className="event-uploader-event-image"
                                    />
                                </div>
                            )}
                            <div className="event-uploader-event-content">
                                <h3>{ev.event_name}</h3>
                                <p>{ev.event_description}</p>
                                <p>
                                    <strong>Date:</strong>{' '}
                                    {new Date(ev.event_date).toLocaleString()}
                                </p>

                                {ev.event_artists.length > 0 && (
                                    <div>
                                        <strong>Artists:</strong>
                                        <ul>
                                            {ev.event_artists.map((a, i) => (
                                                <li key={i}>
                                                    <strong>{a.role}</strong>: {a.artist_name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="event-uploader-card-buttons">
                                    <button
                                        onClick={() => handleEdit(ev)}
                                        className="event-uploader-edit-button"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(ev.id)}
                                        className="event-uploader-delete-button"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="event-uploader-no-events">No events available.</p>
            )}
        </div>
    );
};

export default EventUploader;
